/** Approved placement statuses (SA final-approved, on Placements record). */
export const APPROVED_STATUSES = ['approved', 'sa_approved'];

/** Pending SA final approval (in Review Queue until admin approves). */
export const PENDING_SA_STATUSES = ['manager_approved'];

/** Rejected / backout statuses. */
export const REJECTED_STATUSES = ['rejected', 'backed_out'];

/** Review queue filter groups — must stay in sync with backend REVIEW_QUEUE_STATUS_GROUPS. */
export const REVIEW_QUEUE_STATUS_GROUPS = {
  pending_lead: ['submitted', 'pending_lead', 'pending_lead_review', 'reviewing'],
  pending_manager: [
    'submitted', 'reviewing', 'pending_lead', 'pending_lead_review',
    'lead_approved', 'pending_manager', 'pending_manager_review',
  ],
  pending_sa: ['manager_approved'],
  pending_review: [
    'submitted', 'pending_lead', 'pending_lead_review', 'reviewing',
    'lead_approved', 'pending_manager', 'pending_manager_review', 'manager_approved',
  ],
  approved_placements: ['approved', 'sa_approved'],
};

/** Map payroll filter values to actual DB status values. */
export const PAYROLL_STATUS_GROUPS = {
  approved: APPROVED_STATUSES,
  pending_lead: ['submitted', 'pending_lead', 'pending_lead_review', 'reviewing', 'draft'],
  pending_manager: [
    'submitted', 'reviewing', 'lead_approved', 'pending_manager', 'pending_manager_review',
  ],
  pending_sa: PENDING_SA_STATUSES,
  rejected: REJECTED_STATUSES,
  backed_out: REJECTED_STATUSES,
  sa_approved: ['sa_approved'],
  draft: ['draft'],
};

export function matchesReviewQueueFilter(status, filterValue) {
  if (!filterValue || filterValue === 'all') return true;
  const group = REVIEW_QUEUE_STATUS_GROUPS[filterValue];
  if (group) return group.includes(status);
  return status === filterValue;
}

/** Admin = superadmin or ceo role (any user with admin role, not a single email). */
export function isAdminRole({ isSuperadmin, isCeo }) {
  return isSuperadmin || isCeo;
}

/** True when placement has no dedicated team manager (lead may stand in). */
export function placementHasNoManager(placement) {
  if (placement?.has_assigned_manager === true) return false;
  if (placement?.has_assigned_manager === false) return true;
  const assigned = String(placement?.assigned_manager_id || '').trim().toLowerCase();
  return !assigned || ['none', 'null', 'undefined', 'unassigned', 'n/a', '-'].includes(assigned);
}

export function canActOnReviewQueueItem(user, placement, { isSuperadmin, isCeo, isManager, isLead }, action = 'approve') {
  // Prefer server-computed flags when present (authoritative)
  if (action === 'approve' && placement?.can_approve !== undefined) {
    return Boolean(placement.can_approve);
  }
  if (action === 'reject' && placement?.can_reject !== undefined) {
    return Boolean(placement.can_reject);
  }

  const status = placement?.status;
  const isSelf = String(placement?.created_by || '') === String(user?.id || '');
  const isAdmin = isAdminRole({ isSuperadmin, isCeo });
  const noManager = placementHasNoManager(placement);
  const module = placement?.module || 'sales';
  const role = user?.role || '';
  const isModuleManager =
    (role === 'sales_manager' && module === 'sales') ||
    (role === 'recruiting_manager' && module === 'recruiting');
  const isModuleLead =
    (role === 'sales_lead' && module === 'sales') ||
    (role === 'recruiting_lead' && module === 'recruiting');

  if (action === 'reject') {
    if (isAdmin) {
      return REVIEW_QUEUE_STATUS_GROUPS.pending_review.includes(status);
    }
    if (isModuleManager && !isSelf) {
      return REVIEW_QUEUE_STATUS_GROUPS.pending_manager.includes(status);
    }
    if (isModuleLead && !isSelf && noManager) {
      return REVIEW_QUEUE_STATUS_GROUPS.pending_manager.includes(status);
    }
    return false;
  }

  if (isAdmin) {
    // Admins can approve at any pending stage
    return REVIEW_QUEUE_STATUS_GROUPS.pending_review.includes(status);
  }
  if (isModuleManager && !isSelf) {
    return REVIEW_QUEUE_STATUS_GROUPS.pending_manager.includes(status);
  }
  if (isModuleLead && !isSelf && noManager) {
    return REVIEW_QUEUE_STATUS_GROUPS.pending_manager.includes(status);
  }
  return false;
}

export function matchesPayrollStatus(status, filterValue) {
  if (!filterValue || filterValue === 'all') return true;
  const group = PAYROLL_STATUS_GROUPS[filterValue];
  if (group) return group.includes(status);
  return status === filterValue;
}

export function isApprovedStatus(status) {
  return APPROVED_STATUSES.includes(status);
}

export function isRejectedStatus(status) {
  return REJECTED_STATUSES.includes(status);
}

export function isInReviewQueue(status) {
  return REVIEW_QUEUE_STATUS_GROUPS.pending_review.includes(status);
}
